/**
 *
 * ChallengeQuestionLayout
 *
 */

import { stylize } from "@envestnet/envreact-component-library";
import classnames from "classnames";
import React, { useEffect } from "react";
import withIntl from "utils/withIntl";

import AccountLocked from "components/AccountLocked";
import CentricLayout from "components/CentricLayout";
import ChallengeQuestionForm from "components/ChallengeQuestionForm";
import Logo from "images/Logo";
import styles, { Styles } from "./styles";

interface InjectedProps extends Styles {
  /** Internal - injected from WithStyles */
  innerRef?: React.RefObject<HTMLDivElement>;
}

interface DefaultProps {
  // Default props should always be required props, i.e. no ? indicating it's optional
  // The default values are specified below

  /** Description of this prop */
  challengeQuestion: string;
  error: boolean;
  isLoading: boolean;
  token: string;
  noOfRetries: number;
  hasEmailError: boolean;
  isInvalidCode: boolean;
  hasEmailExpired: boolean;
  loadChallengeQuestion: Function;
  setActivationCode: Function;
  onAnswerSubmission: Function;
  setToken: Function;
}

export interface ChallengeQuestionLayoutProps extends DefaultProps {
  /** CSS class name to be applied to top level component */
  className?: string;

  /** Inline style object to be applied to top level component */
  style?: React.CSSProperties;

  /** Description of this prop */
  goToPasswordSetup: Function;
}

const defaultProps: DefaultProps = {
  challengeQuestion: "No challenge question configured",
  error: false,
  isLoading: false,
  token: "",
  noOfRetries: 3,
  hasEmailError: false,
  isInvalidCode: false,
  hasEmailExpired: false,
  loadChallengeQuestion: () => {},
  setActivationCode: () => {},
  onAnswerSubmission: () => {},
  setToken: () => {},
};

type Props = InjectedProps & ChallengeQuestionLayoutProps;

const ChallengeQuestionLayout = (props: Props) => {
  const {
    classes,
    style,
    className,
    innerRef,
    loadChallengeQuestion,
    challengeQuestion,
    setActivationCode,
    goToPasswordSetup,
    onAnswerSubmission,
    token,
    setToken,
    isLoading,
    noOfRetries,
    hasEmailError,
    isInvalidCode: isCodeInvalid,
    hasEmailExpired,
    error,
  } = props;
  const [isAccountLocked, setAccountLocked] = React.useState<boolean>(false);
  const [isInvalidCode, setInvalidCodeValue] = React.useState(false);
  const activationCode = new URLSearchParams(window.location.search).get(
    "activationCode"
  );

  useEffect(() => {
    if (activationCode) {
      loadChallengeQuestion(activationCode);
      setActivationCode(activationCode);
    } else {
      setInvalidCodeValue(true);
    }
  }, []);

  useEffect(() => {
    if (noOfRetries === 0) {
      setAccountLocked(true);
    }
  }, [noOfRetries]);

  useEffect(() => {
    if (isCodeInvalid) {
      setInvalidCodeValue(true);
    }
  }, [isCodeInvalid]);

  useEffect(() => {
    if (token) {
      setToken(token);
      goToPasswordSetup();
    }
  }, [token]);

  return (
    <div
      ref={innerRef}
      className={classnames(className, classes.container)}
      style={style}
    >
      <CentricLayout>
        <div>
          {hasEmailExpired || error ? (
            <div>
              <Logo />
              {hasEmailExpired && <AccountLocked type="EMAIL_EXPIRED" />}
              {error && <AccountLocked type="ERROR" />}
            </div>
          ) : (
            <div>
              <Logo />
              {!isAccountLocked && !isInvalidCode ? (
                <ChallengeQuestionForm
                  question={challengeQuestion}
                  onSubmit={onAnswerSubmission}
                  loading={isLoading}
                  hasEmailError={hasEmailError}
                  noOfRetries={noOfRetries}
                />
              ) : (
                <AccountLocked
                  type={isInvalidCode ? "INVALID_CODE" : "ACCOUNT_LOCKED"}
                />
              )}
            </div>
          )}
        </div>
      </CentricLayout>
    </div>
  );
};

ChallengeQuestionLayout.defaultProps = defaultProps;

export default withIntl(
  stylize<Props, DefaultProps>(styles)(ChallengeQuestionLayout)
);
